import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';

const API_BASE_URL =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';

export default function SettingsEmailTracking() {
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const userIdHeader = contextUser?.user_id || contextUser?.id || '';
  const tenantIdHeader = resolveEffectiveTenant(
    contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    contextTenant,
    contextUser,
    'instructure'
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [trackingEnabled, setTrackingEnabled] = useState(true);
  const [replyToEnabled, setReplyToEnabled] = useState(true);

  useEffect(() => {
    if (contextUser && tenantIdHeader) load();
  }, [contextUser, tenantIdHeader]);

  const load = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/settings/user`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userIdHeader,
          'x-tenant-id': tenantIdHeader,
        },
      });
      if (res.ok) {
        const data = await res.json();
        const trk = data?.settings?.email_tracking_enabled;
        const rep = data?.settings?.email_replyto_enabled;
        setTrackingEnabled(trk === undefined || trk === null ? true : !!trk);
        setReplyToEnabled(rep === undefined || rep === null ? true : !!rep);
      }
    } catch (e) {
      console.error('Error loading tracking pref:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const saveField = async (field, next) => {
    const res = await fetch(`${API_BASE_URL}/api/settings/user`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-user-id': userIdHeader,
        'x-tenant-id': tenantIdHeader,
      },
      // Use the handler param (next), never stale state.
      body: JSON.stringify({ [field]: next }),
    });
    if (!res.ok) throw new Error('save failed');
  };

  const onTrackingToggle = async (next) => {
    setTrackingEnabled(next);
    setIsSaving(true);
    try {
      await saveField('email_tracking_enabled', next);
      toast.success(next ? 'Tracking enabled' : 'Tracking disabled');
    } catch (e) {
      console.error('Error saving tracking pref:', e);
      setTrackingEnabled(!next);
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const onReplyToToggle = async (next) => {
    setReplyToEnabled(next);
    setIsSaving(true);
    try {
      await saveField('email_replyto_enabled', next);
      toast.success(next ? 'Reply tracking enabled' : 'Reply tracking disabled');
    } catch (e) {
      console.error('Error saving reply-to pref:', e);
      setReplyToEnabled(!next);
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Eye className="w-5 h-5" />
          Email Delivery Privacy
        </CardTitle>
        <CardDescription>
          Control how outbound emails are modified for analytics. Disabling reduces spam-filter risk.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between gap-6">
          <div className="flex-1">
            <Label htmlFor="tracking-toggle" className="text-sm font-medium">
              Open & click tracking
            </Label>
            <p className="text-xs text-gray-500 mt-1">
              Inject 1×1 pixel and rewrite links. When OFF, opens and clicks are not recorded in Analytics.
            </p>
          </div>
          <Switch
            id="tracking-toggle"
            checked={trackingEnabled}
            onCheckedChange={onTrackingToggle}
            disabled={isSaving}
          />
        </div>
        <div className="flex items-center justify-between gap-6 border-t pt-4">
          <div className="flex-1">
            <Label htmlFor="replyto-toggle" className="text-sm font-medium">
              Reply tracking
            </Label>
            <p className="text-xs text-gray-500 mt-1">
              Replace Reply-To header so replies route through our pipeline. When OFF, replies go straight to your mailbox and are not recorded as replies in Analytics.
            </p>
          </div>
          <Switch
            id="replyto-toggle"
            checked={replyToEnabled}
            onCheckedChange={onReplyToToggle}
            disabled={isSaving}
          />
        </div>
      </CardContent>
    </Card>
  );
}
